import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Mail, Lock, Eye, EyeOff, Loader2, Sparkles, Phone, BarChart2 } from 'lucide-react';
import { getDefaultAuthenticatedRoute } from '../lib/userAccess';
import { WaveBackground } from '../components_dashboard/WaveBackground';
import { safeReturnPath } from '../features/invitations/accept-token';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = safeReturnPath(searchParams.get('next'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      navigate(returnPath ?? getDefaultAuthenticatedRoute(user));
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg).join('. '));
      } else {
        setError(detail || 'Login failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#fdfcfb] overflow-hidden flex flex-col lg:flex-row">
      <WaveBackground />
      {/* Warm ambient glows matching the website hero */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 h-[620px] w-[620px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(240,83,15,0.22), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-72 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(217,148,24,0.28), transparent 72%)" }}
      />

      {/* Left Column - Branding & Copy */}
      <div className="relative z-10 flex flex-col justify-center flex-1 px-8 py-12 lg:px-16 xl:px-24">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-12">
            <div className="flex items-center justify-center rounded-xl bg-[#e14e0e] w-12 h-12 shadow-sm">
              <span className="text-xl font-bold text-white">T</span>
            </div>
            <span className="text-2xl font-bold tracking-tight text-slate-900">TechLadder.ai</span>
          </div>

          <h1 className="text-4xl font-medium leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl mb-6">
            Voice AI operations, <br className="hidden lg:block" />
            in one workspace.
          </h1>

          <p className="max-w-lg mb-10 text-lg leading-relaxed text-slate-600">
            Build, schedule, monitor, and improve every agent from a unified dashboard.
          </p>

          <div className="flex flex-col items-start gap-4">
            <div className="flex items-center gap-3 px-5 py-2.5 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-full shadow-sm text-slate-700 font-medium text-sm">
              <Sparkles className="w-4 h-4 text-[#e14e0e]" />
              Agent Management
            </div>
            <div className="flex items-center gap-3 px-5 py-2.5 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-full shadow-sm text-slate-700 font-medium text-sm">
              <Phone className="w-4 h-4 text-[#e14e0e]" />
              Campaign Operations
            </div>
            <div className="flex items-center gap-3 px-5 py-2.5 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-full shadow-sm text-slate-700 font-medium text-sm">
              <BarChart2 className="w-4 h-4 text-[#e14e0e]" />
              Call Analytics
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Login Form */}
      <div className="relative z-10 flex flex-col justify-center flex-1 px-8 py-12 lg:px-16 xl:px-24 items-center lg:items-end">
        <div className="w-full max-w-[420px]">
          <div className="p-8 bg-white border border-slate-100 rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1),0_0_20px_rgba(0,0,0,0.02)]">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
              <p className="mt-2 text-sm text-slate-500">
                {returnPath ? 'Sign in with the address the invite was sent to' : 'Sign in to continue to your workspace'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="demo@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 border-slate-200 bg-slate-50/50 focus:bg-white focus:border-[#e14e0e] focus:ring-[#e14e0e]"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                  <Link to="/forgot-password" className="text-sm font-medium transition-colors text-[#e14e0e] hover:text-[#c4410a]">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11 border-slate-200 bg-slate-50/50 focus:bg-white focus:border-[#e14e0e] focus:ring-[#e14e0e]"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute -translate-y-1/2 right-3 top-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full font-medium text-white rounded-lg h-11 bg-[#e14e0e] hover:bg-[#c4410a] shadow-sm transition-all"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-xs font-medium text-slate-400">
              © 2026 TechLadder.ai. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
